/**
 * Exponential backoff retry utility
 * Handles transient failures with increasing delays
 *
 * Usage:
 * ```typescript
 * const result = await retryWithBackoff(
 *   async () => await apiCall(),
 *   { maxRetries: 5, baseDelayMs: 1000 }
 * );
 * ```
 */

export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  jitter?: boolean;
  retryableErrors?: number[]; // HTTP status codes to retry
}

export class RetryableError extends Error {
  public statusCode?: number;
  public retryAfter?: number;

  constructor(
    message: string,
    statusCode?: number,
    retryAfter?: number
  ) {
    super(message);
    this.name = 'RetryableError';
    this.statusCode = statusCode;
    this.retryAfter = retryAfter;
  }
}

/**
 * Retry a function with exponential backoff
 *
 * @param fn - Function to retry
 * @param options - Retry configuration
 * @returns Result of successful function call
 * @throws Last error if all retries exhausted
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 5,
    baseDelayMs = 1000,
    maxDelayMs = 30000,
    jitter = true,
    retryableErrors = [429, 500, 502, 503, 504],
  } = options;

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;

      // Check if error is retryable
      const isRetryable =
        error instanceof RetryableError ||
        (error.status && retryableErrors.includes(error.status)) ||
        (error.statusCode && retryableErrors.includes(error.statusCode));

      if (!isRetryable || attempt === maxRetries) {
        throw error; // Not retryable or max retries reached
      }

      // Calculate delay with exponential backoff
      let delay = Math.min(baseDelayMs * Math.pow(2, attempt), maxDelayMs);

      // Add jitter (random ±25%)
      if (jitter) {
        const jitterAmount = delay * 0.25;
        delay += (Math.random() * 2 - 1) * jitterAmount;
      }

      // Respect Retry-After header if present
      if (error.retryAfter) {
        delay = Math.max(delay, error.retryAfter * 1000);
      }

      console.log(
        `[Retry] Attempt ${attempt + 1}/${maxRetries} failed, retrying in ${Math.round(delay)}ms...`,
        error.message
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Circuit breaker to prevent repeated failures
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Too many failures, reject requests immediately
 * - HALF_OPEN: Testing if service recovered
 */
export class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  private threshold: number;
  private timeout: number;

  constructor(
    threshold = 5, // Failures before opening circuit
    timeout = 60000 // Time to wait before half-open (ms)
  ) {
    this.threshold = threshold;
    this.timeout = timeout;
  }

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      const now = Date.now();
      if (now - this.lastFailureTime > this.timeout) {
        console.log('[Circuit Breaker] Transitioning to half-open');
        this.state = 'half-open';
      } else {
        throw new Error('Circuit breaker is OPEN - too many failures. Please try again later.');
      }
    }

    try {
      const result = await fn();

      // Success - reset if half-open
      if (this.state === 'half-open') {
        console.log('[Circuit Breaker] Transitioning to closed');
        this.state = 'closed';
        this.failures = 0;
      }

      return result;
    } catch (error) {
      this.failures++;
      this.lastFailureTime = Date.now();

      if (this.failures >= this.threshold) {
        console.log('[Circuit Breaker] Transitioning to open');
        this.state = 'open';
      }

      throw error;
    }
  }

  reset(): void {
    this.failures = 0;
    this.state = 'closed';
  }

  getState(): string {
    return this.state;
  }

  getFailures(): number {
    return this.failures;
  }
}

/**
 * Simple rate limiter to prevent API quota exhaustion
 * Tracks requests and enforces per-window limits
 */
export interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
}

export class RateLimiter {
  private requests: number[] = [];
  private config: RateLimitConfig;

  constructor(config: RateLimitConfig) {
    this.config = config;
  }

  async throttle(): Promise<void> {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Remove old requests outside the window
    this.requests = this.requests.filter((time) => time > windowStart);

    if (this.requests.length >= this.config.maxRequests) {
      // Calculate how long to wait
      const oldestRequest = this.requests[0];
      const waitTime = oldestRequest + this.config.windowMs - now;

      if (waitTime > 0) {
        console.log(`[Rate Limiter] Limit reached, waiting ${waitTime}ms`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));
      }
    }

    this.requests.push(now);
  }

  reset(): void {
    this.requests = [];
  }

  getRequestCount(): number {
    const now = Date.now();
    const windowStart = now - this.config.windowMs;
    return this.requests.filter((time) => time > windowStart).length;
  }
}

// Google Drive API: 20,000 requests per 100 seconds per user
// We'll be conservative but reasonable: 200 requests per 100 seconds
// This allows ~2 requests/second average, which should handle normal sync patterns
export const driveRateLimiter = new RateLimiter({
  maxRequests: 200,
  windowMs: 100 * 1000, // 100 seconds
});
